import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';
import './FinalizeBookingPage.css'; // We'll create this CSS file next

interface BookingState {
    startDate: Date;
    endDate: Date;
    numGuests: number;
    totalPrice: number;
    suiteType: string;
}

const FinalizeBookingPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    // Location state might be null if accessed directly, so we handle that
    const bookingState = location.state as BookingState;

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    if (!bookingState) {
        return (
            <div className="finalize-booking-container">
                <p>No booking details found. Please go back to the <a href="/booking">booking page</a>.</p>
            </div>
        );
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const response = await fetch(`${API_URL}/api/bookings/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    suiteType: bookingState.suiteType,
                    startDate: bookingState.startDate,
                    endDate: bookingState.endDate,
                    numGuests: bookingState.numGuests,
                    totalPrice: bookingState.totalPrice,
                    guestName: `${formData.firstName} ${formData.lastName}`,
                    guestEmail: formData.email,
                    guestPhone: formData.phone,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to submit booking request');
            }

            setSubmitStatus('success');
            // Optionally clear form or redirect after a delay
        } catch (error) {
            console.error("Error submitting booking request:", error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitStatus === 'success') {
        return (
            <div className="finalize-booking-page container success-view">
                <h2>{t('finalizeBookingPage.successMessage')}</h2>
                <button className="return-home-btn" onClick={() => navigate('/')}>Return to Home</button>
            </div>
        );
    }

    return (
        <div className="finalize-booking-page container section-container fade-in px-4 md:px-0">
            <div className="finalize-header flex items-center justify-between mb-8 cursor-pointer text-secondary hover:text-primary transition-colors w-fit" onClick={() => navigate('/booking')}>
                <span className="material-symbols-outlined mr-2">arrow_back</span>
                <span className="font-bold text-sm tracking-widest uppercase">{t('adminPage.cancel') || 'Back'}</span>
            </div>

            <div className="finalize-header">
                <span className="section-label">{t('bookingPage.heroLabel')}</span>
                <h1 className="text-3xl md:text-5xl font-light text-primary mt-2">{t('finalizeBookingPage.title')}</h1>
                <p className="text-xl text-on-surface-variant mt-4 max-w-2xl">{t('finalizeBookingPage.intro')}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mt-12 items-start">
                <div className="lg:col-span-5 bg-surface-container-low p-6 md:p-8 rounded-2xl border border-outline-variant/10 shadow-[0_10px_40px_rgba(27,28,25,0.02)] lg:sticky top-32">
                    <h3 className="text-3xl font-headline text-primary mb-6 border-b border-surface-container-highest pb-4">{t('finalizeBookingPage.summaryTitle')}</h3>
                    <div className="flex flex-col space-y-4">
                        <div className="flex flex-col items-start bg-surface-container-lowest p-6 rounded-xl border border-outline-variant/10 cursor-pointer shadow-sm hover:border-secondary transition-all">
                            <span className="block text-xs font-bold text-secondary uppercase tracking-widest mb-4">{t('bookingPage.checkIn')} & {t('bookingPage.checkOut')}</span>
                            <div className="flex flex-col space-y-2 mt-auto">
                                <span className="font-headline text-xl text-primary break-words">{new Date(bookingState.startDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                <span className="text-secondary text-sm font-bold pl-2">↓</span>
                                <span className="font-headline text-xl text-primary break-words">{new Date(bookingState.endDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            </div>
                        </div>
                        <div className="flex flex-col items-start bg-surface-container-lowest p-6 rounded-xl border border-outline-variant/10 shadow-sm">
                            <span className="block text-xs font-bold text-secondary uppercase tracking-widest mb-2">{t('finalizeBookingPage.summary.guests')}</span>
                            <span className="font-headline text-3xl text-primary mt-auto">{bookingState.numGuests}</span>
                        </div>
                        <div className="flex flex-col items-start bg-primary/5 p-6 rounded-xl border border-primary/10 shadow-sm">
                            <span className="block text-xs font-bold text-secondary uppercase tracking-widest mb-2">{t('finalizeBookingPage.summary.total')}</span>
                            <span className="font-headline text-4xl font-bold text-primary mt-auto">€{bookingState.totalPrice.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="lg:col-span-7 bg-surface-container-lowest p-6 md:p-8 rounded-2xl shadow-[0_10px_40px_rgba(27,28,25,0.03)] border border-outline-variant/10">
                    <h3 className="font-headline text-3xl text-primary mb-8">{t('finalizeBookingPage.formTitle')}</h3>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                        <div className="flex flex-col space-y-2">
                            <label className="text-sm font-bold text-on-surface-variant" htmlFor="firstName">{t('finalizeBookingPage.form.firstName')}</label>
                            <input
                                className="bg-surface border border-outline-variant/50 focus:border-secondary focus:ring-1 focus:ring-secondary p-4 rounded-xl text-lg text-primary outline-none transition-all"
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="flex flex-col space-y-2">
                            <label className="text-sm font-bold text-on-surface-variant" htmlFor="lastName">{t('finalizeBookingPage.form.lastName')}</label>
                            <input
                                className="bg-surface border border-outline-variant/50 focus:border-secondary focus:ring-1 focus:ring-secondary p-4 rounded-xl text-lg text-primary outline-none transition-all"
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="flex flex-col space-y-2 xl:col-span-2">
                            <label className="text-sm font-bold text-on-surface-variant" htmlFor="email">{t('finalizeBookingPage.form.email')}</label>
                            <input
                                className="bg-surface border border-outline-variant/50 focus:border-secondary focus:ring-1 focus:ring-secondary p-4 rounded-xl text-lg text-primary outline-none transition-all"
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="flex flex-col space-y-2 xl:col-span-2">
                            <label className="text-sm font-bold text-on-surface-variant" htmlFor="phone">{t('finalizeBookingPage.form.phone')}</label>
                            <input
                                className="bg-surface border border-outline-variant/50 focus:border-secondary focus:ring-1 focus:ring-secondary p-4 rounded-xl text-lg text-primary outline-none transition-all"
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    {submitStatus === 'error' && <p className="error-message" style={{ color: 'var(--error)', marginTop: 'var(--spacing-4)' }}>{t('finalizeBookingPage.errorMessage')}</p>}

                    <button type="submit" disabled={isSubmitting} className="submit-btn" style={{ marginTop: 'var(--spacing-8)' }}>
                        {isSubmitting ? t('finalizeBookingPage.form.submitting') : t('finalizeBookingPage.submitButton')}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default FinalizeBookingPage;
